import React from 'react';
import { History, Award, Users, Target, Leaf, TrendingUp } from 'lucide-react';

const Histoire = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <History className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Qui est Ecopra ?
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Une solution française pour des moteurs plus propres et plus économiques
            </p>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Une solution française pour des moteurs plus propres et plus économiques
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Cette entreprise française innovante développe une technologie unique : <strong>le Kit Ecopra</strong>. 
            Ce dispositif améliore la combustion des moteurs thermiques. Il permet de consommer moins de carburant 
            et de réduire les émissions polluantes.
          </p>
          
          {/* Logo/Slogan Section */}
          <div className="text-center my-12">
            <div className="inline-block bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10 rounded-xl p-8">
              <p className="text-4xl font-bold bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent mb-2">
                ecopra
              </p>
              <p className="text-xl text-gray-600 italic">
                Faites le plein d'air
              </p>
            </div>
          </div>
        </div>

        {/* Fabrication 100% Française */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Award className="w-8 h-8 text-[#00BCD4]" />
            <h2 className="text-3xl font-bold text-gray-800">
              Une fabrication 100% française, enracinée en Auvergne
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Conçu et assemblé au cœur du <strong>Livradois-Forez</strong>, le système Ecopra incarne une vision 
                industrielle profondément locale. Chaque pièce, chaque assemblage est le fruit d'un savoir-faire 
                français, mobilisant un réseau d'artisans, d'ingénieurs et d'entrepreneurs engagés dans une même 
                quête : conjuguer performance mécanique, sobriété énergétique et responsabilité environnementale.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                En collaborant avec des entreprises régionales spécialisées en mécanique de précision, écoconception 
                et innovation durable, Ecopra tisse un écosystème local où générations et expertises s'allient. 
                Ce modèle court favorise non seulement la traçabilité et la qualité, mais participe activement à la 
                résilience économique du territoire.
              </p>
              <div className="bg-blue-50 border-l-4 border-[#00BCD4] p-4 mt-4">
                <p className="text-gray-800">
                  <strong>Entreprise ambassadrice du parc Livradois-Forez</strong>, Ecopra porte fièrement les 
                  valeurs de son territoire : respect de l'environnement, transmission des savoirs, et engagement 
                  collectif. Ici, le "Made in France" n'est pas une étiquette, mais une pratique quotidienne, 
                  une exigence de fond, une conviction incarnée.
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="bg-yellow-50 border-4 border-yellow-400 rounded-lg p-8 text-center">
                <div className="text-6xl font-bold text-yellow-600 mb-4">
                  AUVERGNE
                </div>
                <div className="text-2xl text-yellow-700">
                  🏔️🏭🇫🇷
                </div>
                <p className="text-sm text-yellow-700 mt-4 font-semibold">
                  Fabrication Française<br/>Livradois-Forez
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Histoire COPRA/Ecopra */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <History className="w-8 h-8 text-[#7CB342]" />
            <h2 className="text-3xl font-bold text-gray-800">
              copra / ecopra - Une histoire ancrée dans l'innovation
            </h2>
          </div>

          <div className="space-y-6">
            <div className="border-l-4 border-[#7CB342] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-[#7CB342] text-white font-bold px-4 py-2 rounded-full">
                  1962
                </span>
                <h3 className="text-xl font-bold text-gray-800">Fondation de COPRA</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                L'aventure commence dans la coopérative <strong>COPRA</strong>, fondée en <strong>1962</strong> dans 
                le bassin minier du Puy-de-Dôme.
              </p>
            </div>

            <div className="border-l-4 border-[#00BCD4] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-[#00BCD4] text-white font-bold px-4 py-2 rounded-full">
                  2007
                </span>
                <h3 className="text-xl font-bold text-gray-800">Premiers kits à monter soi-même</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                En 2007, les premiers kits à monter soi-même voient le jour.
              </p>
            </div>

            <div className="border-l-4 border-[#7CB342] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-[#7CB342] text-white font-bold px-4 py-2 rounded-full">
                  2008
                </span>
                <h3 className="text-xl font-bold text-gray-800">Ecopra devient une société à part entière</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Dès <strong>2008</strong>, <strong>Ecopra devient une société à part entière</strong>. L'année 
                suivante, elle propose le premier économiseur de carburant installé sur des voitures neuves, 
                sans annuler la garantie constructeur.
              </p>
            </div>
          </div>
        </div>

        {/* Technologie Simple et Universelle */}
        <div className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 rounded-xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Une technologie simple et universelle
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="text-4xl mb-3">🔧</div>
              <h3 className="font-bold text-gray-800 mb-2">Compatible</h3>
              <p className="text-sm text-gray-600">
                Tous types de moteurs thermiques
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-bold text-gray-800 mb-2">Installation simple</h3>
              <p className="text-sm text-gray-600">
                Sans modifier le moteur
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="text-4xl mb-3">🔄</div>
              <h3 className="font-bold text-gray-800 mb-2">Transférable</h3>
              <p className="text-sm text-gray-600">
                D'un véhicule à un autre
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="text-4xl mb-3">✨</div>
              <h3 className="font-bold text-gray-800 mb-2">Sans entretien</h3>
              <p className="text-sm text-gray-600">
                Aucun entretien nécessaire
              </p>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed">
            Le Kit Ecopra s'adapte à tous types de moteurs thermiques. Il s'installe sans modifier le moteur. 
            Il ne nécessite aucun entretien. Mieux encore : il peut être transféré facilement d'un véhicule à 
            un autre. Aujourd'hui, il est utilisé dans les secteurs de <strong>l'automobile</strong>, 
            du <strong>transport</strong>, de <strong>l'agriculture</strong> et de <strong>l'industrie</strong>.
          </p>
        </div>

        {/* Réseau de partenaires */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-8 h-8 text-[#00BCD4]" />
            <h2 className="text-3xl font-bold text-gray-800">
              Un réseau de partenaires engagé
            </h2>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            Pour développer et diffuser sa technologie, Ecopra s'appuie sur un <strong>réseau de partenaires</strong>. 
            Ce réseau comprend des garages, des installateurs agréés, des entreprises spécialisées dans la transition 
            énergétique, ainsi que des centres de recherche. Grâce à cette collaboration, Ecopra assure un accompagnement 
            de qualité, une installation professionnelle et une diffusion efficace de ses solutions.
          </p>

          <p className="text-gray-700 leading-relaxed">
            Ces partenaires partagent les valeurs d'Ecopra : <strong>innovation, écologie, performance et fiabilité</strong>. 
            Ensemble, ils contribuent à rendre la technologie du Kit accessible au plus grand nombre, en France et à 
            l'international.
          </p>
        </div>

        {/* Mission Ecopra */}
        <div className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] rounded-xl p-8 text-white mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-8 h-8" />
            <h2 className="text-3xl font-bold">
              ecopra - Une mission claire : économie, écologie, durabilité
            </h2>
          </div>

          <p className="text-lg leading-relaxed mb-8 opacity-95">
            Ecopra poursuit un objectif simple : rendre les moteurs plus propres, plus performants et plus accessibles. 
            Grâce à sa technologie brevetée, son engagement local et son approche durable, Ecopra propose une solution 
            concrète à un défi mondial : réduire l'impact environnemental tout en réalisant des économies.
          </p>

          {/* 3 Piliers */}
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">ÉCONOMIE</h3>
              <p className="text-sm opacity-90">
                Réduction significative de la consommation de carburant
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
              <Leaf className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">ÉCOLOGIE</h3>
              <p className="text-sm opacity-90">
                Réduction des émissions polluantes et du CO₂
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
              <Award className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">DURABILITÉ</h3>
              <p className="text-sm opacity-90">
                Solution durable pour un avenir responsable
              </p>
            </div>
          </div>
        </div>

        {/* Distribution Suisse */}
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            🇨🇭 Distributeur officiel Ecopra pour la Suisse
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            <strong>Steve Rosset</strong> est votre distributeur officiel Ecopra en Suisse. 
            Installation professionnelle disponible partout en Suisse.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/boutique"
              className="inline-block bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white font-bold px-8 py-3 rounded-full hover:shadow-lg transition-all transform hover:scale-105"
            >
              Découvrir nos kits
            </a>
            <a
              href="/contact"
              className="inline-block bg-white border-2 border-[#00BCD4] text-[#00BCD4] font-bold px-8 py-3 rounded-full hover:bg-[#00BCD4] hover:text-white transition-all"
            >
              Nous contacter
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Histoire;
